// src/pages/msme/SigmaStudioKPI.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faEdit,
  faEye,
  faFilePdf,
  faDatabase,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";
import "jspdf-autotable";

const exampleKPIs = [
  {
    id: "KPI-BD-01",
    kpiName: "New Revenue Booked (New Clients)",
    targetDefinition: "Total revenue from newly acquired customers (first-time orders + initial contracts)",
    unit: "₹ Lakh",
    baselineValue: "45",
    currentValue: "",
    targetValue: "120",
    direction: "higher-better",
    trackingPeriodEnd: "2025-12-31",
    reviewPeriod: "Quarterly",
    weightage: 15,
    remarks: "Primary growth driver – focus on new brand owners in FMCG/pharma",
  },
  {
    id: "KPI-BD-02",
    kpiName: "Qualified Leads Generated",
    targetDefinition: "Number of Marketing Qualified Leads (MQLs) + Sales Qualified Leads (SQLs) from outbound, inbound, exhibitions, referrals",
    unit: "leads",
    baselineValue: "85",
    currentValue: "",
    targetValue: "220",
    direction: "higher-better",
    trackingPeriodEnd: "2025-12-31",
    reviewPeriod: "Monthly",
    weightage: 10,
    remarks: "Target mix: 40% inbound (website/enquiry), 60% outbound & events",
  },
  {
    id: "KPI-BD-03",
    kpiName: "Lead to Opportunity Conversion Rate",
    targetDefinition: "(Number of opportunities created / Number of qualified leads) × 100",
    unit: "%",
    baselineValue: "28",
    currentValue: "",
    targetValue: "42",
    direction: "higher-better",
    trackingPeriodEnd: "2025-12-31",
    reviewPeriod: "Quarterly",
    weightage: 12,
    remarks: "Improve qualification process and early needs discovery",
  },
  {
    id: "KPI-BD-04",
    kpiName: "Win Rate (Opportunity Close Rate)",
    targetDefinition: "(Number of won deals / Total closed opportunities) × 100",
    unit: "%",
    baselineValue: "32",
    currentValue: "",
    targetValue: "48",
    direction: "higher-better",
    trackingPeriodEnd: "2025-12-31",
    reviewPeriod: "Quarterly",
    weightage: 14,
    remarks: "Critical – target improvement via better proposal customization & value selling",
  },
  {
    id: "KPI-BD-05",
    kpiName: "Average Deal Size (New + Expansion)",
    targetDefinition: "Average revenue per closed-won deal (including upsell/cross-sell to existing)",
    unit: "₹ Lakh",
    baselineValue: "8.2",
    currentValue: "",
    targetValue: "14.5",
    direction: "higher-better",
    trackingPeriodEnd: "2025-12-31",
    reviewPeriod: "Monthly",
    weightage: 11,
    remarks: "Focus on strategic accounts and multi-SKU/label type contracts",
  },
  {
    id: "KPI-BD-06",
    kpiName: "Sales Pipeline Value (Weighted)",
    targetDefinition: "Sum of (opportunity value × probability %) across active pipeline",
    unit: "₹ Lakh",
    baselineValue: "380",
    currentValue: "",
    targetValue: "950",
    direction: "higher-better",
    trackingPeriodEnd: "2025-12-31",
    reviewPeriod: "Monthly",
    weightage: 13,
    remarks: "Maintain healthy 3–4× coverage of quarterly target",
  },
];

const initialKPI = {
  id: "",
  kpiName: "",
  targetDefinition: "",
  unit: "",
  baselineValue: "",
  currentValue: "",
  targetValue: "",
  trackingPeriodEnd: "",
  reviewPeriod: "",
  weightage: "",
  remarks: "",
};

const SigmaStudioKPI = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [header, setHeader] = useState({
    personName: "",
    designation: "",
    date: new Date().toISOString().split("T")[0],
    version: "1.0",
    remarks: "",
  });

  const [kpis, setKpis] = useState([]);
  const [form, setForm] = useState(initialKPI);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showTableView, setShowTableView] = useState(false);

  const handleHeaderChange = (e) => {
    setHeader({ ...header, [e.target.name]: e.target.value });
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const addOrUpdateKPI = (e) => {
    e.preventDefault();
    if (editingIndex !== null) {
      const updated = [...kpis];
      updated[editingIndex] = { ...form };
      setKpis(updated);
      setEditingIndex(null);
    } else {
      setKpis([...kpis, { ...form, id: form.id || `KPI-${String(kpis.length + 1).padStart(2, "0")}` }]);
    }
    setForm(initialKPI);
  };

  const editKPI = (index) => {
    setForm(kpis[index]);
    setEditingIndex(index);
  };

  const deleteKPI = (index) => {
    if (window.confirm("Delete this KPI?")) {
      setKpis(kpis.filter((_, i) => i !== index));
    }
  };

  const personNameRef = React.useRef(null);

  const populateExample = () => {
    setHeader({
      personName: "Hemant Pathak",
      designation: "Business Development Head",
      date: new Date().toISOString().split("T")[0],
      version: "1.0",
      remarks: "FY 2025-26 – Key Performance Indicators Dashboard (Adhesive Label Manufacturing)",
    });
    setKpis([...exampleKPIs]);
    setShowTableView(true);

    setTimeout(() => {
      personNameRef.current?.focus();
      personNameRef.current?.select();
    }, 100);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("Key Performance Indicators (KPI) Dashboard", 14, 20);

    doc.setFontSize(11);
    doc.text(`Person: ${header.personName || "—"}`, 14, 32);
    doc.text(`Designation: ${header.designation || "—"}`, 14, 38);
    doc.text(`Date: ${header.date || "—"}`, 14, 44);
    doc.text(`Version: ${header.version || "—"}`, 14, 50);
    if (header.remarks) {
      doc.text(`Remarks: ${header.remarks}`, 14, 56);
    }

    const tableColumn = [
      "SN",
      "KPI ID",
      "KPI Name",
      "Target Definition",
      "Unit",
      "Baseline",
      "Target",
      "Current",
      "Period End",
      "Review",
      "Weight",
      "Remarks",
    ];

    const tableRows = kpis.map((kpi, idx) => [
      idx + 1,
      kpi.id,
      kpi.kpiName,
      kpi.targetDefinition,
      kpi.unit,
      kpi.baselineValue || "—",
      kpi.targetValue,
      kpi.currentValue || "—",
      kpi.trackingPeriodEnd || "Ongoing",
      kpi.reviewPeriod,
      kpi.weightage,
      kpi.remarks || "",
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: header.remarks ? 65 : 58,
      styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
      headStyles: { fillColor: [44, 62, 80], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 16 },
        2: { cellWidth: 32 },
        3: { cellWidth: 38 },
        4: { cellWidth: 12 },
        5: { cellWidth: 14 },
        6: { cellWidth: 14 },
        7: { cellWidth: 14 },
        8: { cellWidth: 18 },
        9: { cellWidth: 14 },
        10: { cellWidth: 10 },
        11: { cellWidth: 20 },
      },
    });

    doc.save(`KPI_${header.personName || "Untitled"}_${header.date || "date"}.pdf`);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            🎛️ Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">
            KPI Playground
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">
                {userName}
              </div>
              <button
                onClick={handleLogout}
                className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm"
            >
              🔐
            </button>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-xs lg:text-sm">{userName}</div>
              <button onClick={handleLogout} className="p-2 rounded-md bg-red-600">
                ⏻
              </button>
            </div>
          ) : (
            <div className="flex justify-center">
              <Link to="/login" className="text-teal-300">🔐</Link>
            </div>
          )}
        </div>
      )}

      {/* Header Form */}
      <div className="bg-gray-800 p-5 rounded-lg mb-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm mb-1">Person Name</label>
            <input
              ref={personNameRef}
              name="personName"
              value={header.personName}
              onChange={handleHeaderChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              placeholder="e.g. Hemant Pathak"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Designation</label>
            <input
              name="designation"
              value={header.designation}
              onChange={handleHeaderChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              placeholder="e.g. Production Head"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Date</label>
            <input
              type="date"
              name="date"
              value={header.date}
              onChange={handleHeaderChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Version</label>
            <input
              name="version"
              value={header.version}
              onChange={handleHeaderChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Remarks</label>
            <input
              name="remarks"
              value={header.remarks}
              onChange={handleHeaderChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              placeholder="Appraisal period / FY..."
            />
          </div>
        </div>
      </div>

      {/* KPI Input Form */}
      <div className="bg-gray-800 p-5 rounded-lg mb-6 border border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg text-yellow-200 font-semibold">
            {editingIndex !== null ? "Edit KPI" : "Add New KPI"}
          </h2>
          <button
            onClick={populateExample}
            className="w-64 px-4 py-2 text-white bg-purple-600 hover:bg-purple-700 rounded text-sm flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faDownload} /> <span className="text-white">Populate from Example</span>
          </button>
        </div>

        <form onSubmit={addOrUpdateKPI} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">KPI ID</label>
            <input
              name="id"
              value={form.id}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              placeholder="KPI-01 (auto if empty)"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm mb-1">KPI Name / Focus Area</label>
            <input
              name="kpiName"
              value={form.kpiName}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm mb-1">Target Definition / Formula</label>
            <textarea
              name="targetDefinition"
              value={form.targetDefinition}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              style={{ height: "140px" }}
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Unit of Measure</label>
            <input
              name="unit"
              value={form.unit}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              placeholder="%, pcs/hr, complaints, etc."
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Baseline Value</label>
            <input
              name="baselineValue"
              value={form.baselineValue}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              placeholder="Starting / current level"
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Target Value</label>
            <input
              name="targetValue"
              value={form.targetValue}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Current Value</label>
            <input
              name="currentValue"
              value={form.currentValue}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              placeholder="Latest tracked value"
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Tracking Period End</label>
            <input
              type="date"
              name="trackingPeriodEnd"
              value={form.trackingPeriodEnd}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Review Period</label>
            <select
              name="reviewPeriod"
              value={form.reviewPeriod}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            >
              <option value="">Select...</option>
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Annual">Annual</option>
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">Weightage (1-10)</label>
            <input
              type="number"
              name="weightage"
              min="1"
              max="10"
              value={form.weightage}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm mb-1">Remarks</label>
            <input
              name="remarks"
              value={form.remarks}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            />
          </div>

          <div className="md:col-span-2 flex justify-end gap-3 mt-2">
            {editingIndex !== null && (
              <button
                type="button"
                onClick={() => {
                  setForm(initialKPI);
                  setEditingIndex(null);
                }}
                className="w-32 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="w-40 px-5 py-2 bg-green-600 hover:bg-green-700 rounded flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              {editingIndex !== null ? "Update KPI" : "Add KPI"}
            </button>
          </div>
        </form>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 place-items-center mb-6">
        <button
          onClick={() => setShowTableView(!showTableView)}
          className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 rounded flex items-center gap-2"
          disabled={kpis.length === 0}
        >
          <FontAwesomeIcon icon={faEye} />
          {showTableView ? "Hide" : "View"} KPI Table
        </button>

        <button
          onClick={exportToPDF}
          className="px-5 py-2 bg-red-600 hover:bg-red-700 rounded flex items-center gap-2"
          disabled={kpis.length === 0}
        >
          <FontAwesomeIcon icon={faFilePdf} /> Save as PDF
        </button>

        <button
          disabled
          title="Database integration coming soon"
          className="px-5 py-2 bg-gray-600 rounded flex items-center gap-2 opacity-60 cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faDatabase} /> Save to DB
        </button>
      </div>

      {/* List / Table View */}
      {kpis.length > 0 && (
        <div>
          {showTableView ? (
            <div className="overflow-x-auto bg-gray-800 rounded border border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left">SN</th>
                    <th className="px-4 py-2 text-left">KPI ID</th>
                    <th className="px-4 py-2 text-left">KPI Name</th>
                    <th className="px-4 py-2 text-left">Target</th>
                    <th className="px-4 py-2 text-center">Unit</th>
                    <th className="px-4 py-2 text-center">Current</th>
                    <th className="px-4 py-2 text-center">Weight</th>
                    <th className="px-4 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {kpis.map((kpi, idx) => (
                    <tr key={idx} className="border-t border-gray-700 hover:bg-gray-700">
                      <td className="px-4 py-3">{idx + 1}</td>
                      <td className="px-4 py-3">{kpi.id}</td>
                      <td className="px-4 py-3">{kpi.kpiName}</td>
                      <td className="px-4 py-3">{kpi.targetValue} {kpi.unit}</td>
                      <td className="px-4 py-3 text-center">{kpi.unit}</td>
                      <td className="px-4 py-3 text-center font-bold">{kpi.currentValue || "—"}</td>
                      <td className="px-4 py-3 text-center font-bold">{kpi.weightage}</td>
                      <td className="px-4 py-3 flex justify-center gap-2">
                        <button
                          onClick={() => editKPI(idx)}
                          className="p-1.5 bg-yellow-600 hover:bg-yellow-700 rounded"
                        >
                          <FontAwesomeIcon icon={faEdit} size="sm" />
                        </button>
                        <button
                          onClick={() => deleteKPI(idx)}
                          className="p-1.5 bg-red-600 hover:bg-red-700 rounded"
                        >
                          <FontAwesomeIcon icon={faTrash} size="sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-4">
              {kpis.map((kpi, idx) => (
                <div
                  key={idx}
                  className="bg-gray-800 p-4 rounded border border-gray-700"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-bold">{kpi.id} • {kpi.kpiName}</div>
                      <div className="text-sm text-gray-400 mt-1">
                        Target: <span className="font-semibold text-white">{kpi.targetValue} {kpi.unit}</span> • Weight: <span className="font-semibold text-white">{kpi.weightage}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => editKPI(idx)}
                        className="p-2 bg-yellow-600 hover:bg-yellow-700 rounded"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => deleteKPI(idx)}
                        className="p-2 bg-red-600 hover:bg-red-700 rounded"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mt-3">
                    <div>
                      <span className="text-gray-400">Definition:</span><br />
                      {kpi.targetDefinition}
                    </div>
                    <div>
                      <span className="text-gray-400">Baseline → Target:</span><br />
                      {kpi.baselineValue || "—"} → {kpi.targetValue} {kpi.unit}
                    </div>
                    <div>
                      <span className="text-gray-400">Current:</span><br />
                      {kpi.currentValue || "—"} {kpi.unit}
                    </div>
                    <div>
                      <span className="text-gray-400">Period End:</span><br />
                      {kpi.trackingPeriodEnd || "Ongoing"}
                    </div>
                    <div>
                      <span className="text-gray-400">Review:</span><br />
                      {kpi.reviewPeriod}
                    </div>
                    {kpi.remarks && (
                      <div>
                        <span className="text-gray-400">Remarks:</span><br />
                        {kpi.remarks}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {kpis.length === 0 && (
        <div className="text-center text-gray-400 py-10">
          No KPIs added yet. Click "Add KPI" or "Populate from Example"
        </div>
      )}

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SigmaStudioKPI;